import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { format, startOfQuarter } from 'date-fns'
import styled from 'styled-components'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { utilizationQuarters } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { DetailedWeeklyView } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/detailedWeeklyView/DetailedWeeklyView'

import { useHistoricUtilizationData, useTargetUtilizationData } from './hooks'
import { getBookableTarget, getWorkingHours } from '../../calendar/utils'
import { BCT_ROLES } from '../../common/constants/appConstants'
import {
  getDropdownItemsFromQuarters,
  getPreviousQuartersSorted,
  getYearMonthDayLocaleByDate,
  hasRole,
} from '../../common/utils/utils'
import { getAuthConfig, getAuthEmploymentStatus, getAuthRoles } from '../../data/auth/authSelectors'
import { getRequestedCaseloadManagementQuarter } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { setCurrentQuarterCMD } from '../clients/clientDetails/data/appointmentsSlice'
import { useGetUtilizationQuartersQuery } from '../clients/clientDetails/data/utilizationApi'

const Container = styled.div({
  display: 'flex',
  justifyContent: 'center',
})

const DetailedWeeklyViewContainer: React.FunctionComponent = () => {
  const employmentStatus = useSelector(getAuthEmploymentStatus)
  const userRoles = useSelector(getAuthRoles)
  const config = useSelector(getAuthConfig)
  const requestedQuarter: utilizationQuarters | undefined = useSelector(getRequestedCaseloadManagementQuarter)
  const { showDetailedWeeklyViewForCMD } = useFlags()
  const isLCT = hasRole(userRoles, BCT_ROLES)
  const dispatch = useAppDispatch()

  const { data: utilizationQuarters, isLoading: loadingUtilization } = useGetUtilizationQuartersQuery({
    date: getYearMonthDayLocaleByDate(new Date()),
  })
  const {
    data: historicUtilization,
    isLoading: isLoadingHistoricData,
    isFetching: isRefreshingHistoricUtilization,
  } = useHistoricUtilizationData({
    bookableView: isLCT && showDetailedWeeklyViewForCMD,
  })
  const {
    data: targetUtilization,
    isLoading: isLoadingTargetData,
    isFetching: isRefreshingTargetUtilization,
  } = useTargetUtilizationData()

  const isLoadingUtilizationData = loadingUtilization || isLoadingHistoricData || isLoadingTargetData

  const { previousQuartersSorted, currentQuarter } = useMemo(() => {
    if (!utilizationQuarters) {
      return {
        availableQuarters: {},
        previousQuartersSorted: [],
        originalStartDate: '',
        originalEndDate: '',
        currentQuarter: '',
      }
    }
    const currentAndPreviousQuarters: utilizationQuarters = Object.fromEntries(
      Object.entries(utilizationQuarters)
        .map((quarter: [string, string[]]) => {
          // we are updating our logic starting on Jan 1, 2025 and need this hardcoded. Can remove when the Q1 2025 is not required in quarters dropdown
          // cleanup ticket: https://lyrahealth.atlassian.net/browse/LCDELIVERY-5801
          const quarterNumber = Number(quarter[0].split('_')[0].replace('Q', ''))
          const quarterYear = Number(quarter[0].split('_')[1])
          let quarterRange = quarter[1]
          if (quarterNumber === 1 && quarterYear === 2025) {
            quarterRange = [format(startOfQuarter(new Date(2025, 0)), 'yyyy-MM-dd'), quarter[1][1]]
          }
          return [quarter[0], quarterRange]
        })
        .filter((quarter: [string, string[]]) => Number(quarter[0].split('_')[1]) >= 2025),
    )
    const sorted = getPreviousQuartersSorted({ availableQuarters: currentAndPreviousQuarters })
    return {
      availableQuarters: currentAndPreviousQuarters,
      previousQuartersSorted: sorted,
      originalStartDate: currentAndPreviousQuarters ? currentAndPreviousQuarters[sorted[0]][0] : '',
      originalEndDate: currentAndPreviousQuarters ? currentAndPreviousQuarters[sorted[0]][1] : '',
      currentQuarter: sorted[0],
    }
  }, [utilizationQuarters])

  const dropdownClickHandler = (quarter: string) => {
    if (utilizationQuarters) {
      dispatch(
        setCurrentQuarterCMD({
          [quarter]: utilizationQuarters[quarter],
        }),
      )
    }
  }

  const requestedQuarterName = requestedQuarter ? Object.keys(requestedQuarter)[0] : ''
  let preselectedValue = ''
  if (requestedQuarter) {
    const requestedQuarterName = Object.keys(requestedQuarter)[0]
    const dropdownAppend = requestedQuarterName === currentQuarter ? ' Current' : ''
    preselectedValue = `${requestedQuarterName.replace('_', ' ')}${dropdownAppend}`
  }

  // need a one-off exception to not show the week of 12/29/2024 - 01/04/2025
  //  Clean up when Q1 2025 will no longer show up in Detailed Weekly View
  //  https://lyrahealth.atlassian.net/browse/LCDELIVERY-5803
  const updatedHistoricUtilization = historicUtilization?.filter(
    (utilizationWeek) => utilizationWeek.startDate !== '12/29/2024',
  )

  const expectedWorkingHours =
    (employmentStatus ? config?.inProductCalendar?.workingHours?.[employmentStatus] : undefined) ??
    getWorkingHours(employmentStatus)
  const weeklyExpectedBookable =
    (employmentStatus ? config?.inProductCalendar?.bookableTarget?.[employmentStatus] : undefined) ??
    getBookableTarget(employmentStatus)

  return (
    <Container>
      {isLoadingUtilizationData ? (
        <LoadingIndicator size={45} />
      ) : (
        <DetailedWeeklyView
          historicUtilization={updatedHistoricUtilization}
          targetUtilization={targetUtilization}
          historicQuarter={requestedQuarterName}
          currentQuarter={currentQuarter}
          dropdownItems={getDropdownItemsFromQuarters(previousQuartersSorted, dropdownClickHandler) ?? []}
          expectedWorkingHours={expectedWorkingHours}
          weeklyExpectedBookable={weeklyExpectedBookable}
          preselectedValue={preselectedValue}
          isLoading={isRefreshingHistoricUtilization || isRefreshingTargetUtilization}
        />
      )}
    </Container>
  )
}

export default DetailedWeeklyViewContainer
